"use client";

import { motion, useScroll, useTransform } from "motion/react";
import { type ReactNode, useRef } from "react";

import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";
import { cn } from "@/lib/utils";

// =============================================================================

type ZoomOutProps = {
  children: ReactNode;
  className?: string;
  /** Scale when the element enters the viewport */
  from?: number;
};

/**
 * The content starts zoomed in and zooms out to its natural size as it
 * scrolls into view. The wrapper clips the overflow.
 */
export function ZoomOut({ children, className, from = 1.3 }: ZoomOutProps) {
  const ref = useRef<HTMLDivElement>(null);
  const shouldReduceMotion = usePrefersReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "center 0.55"],
  });
  const scale = useTransform(scrollYProgress, [0, 1], [from, 1]);

  return (
    <div ref={ref} className={cn("relative overflow-hidden", className)}>
      <motion.div
        className="relative size-full"
        style={shouldReduceMotion ? undefined : { scale }}
      >
        {children}
      </motion.div>
    </div>
  );
}

// =============================================================================
